/*
 * File: FeedActionDropdown.jsx
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on February 4, 2022 at 4:30 PM
 * Copyright © 2022 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: May 28, 2025 at 10:37 AM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// Components
import { Typography } from '../Typography';

// Styles
import * as S from './FeedActionDropdown.styles';


/**
 * Component
 */

export const FeedActionDropdown = ({
	className, selected, selectedTitle, title, options, onSelect, multipleSelection, widgetWidth
}) => {

	// Create state handlers
	const [showWidget, setShowWidget] = useState(false);

	// Create refs
	const containerRef = useRef();
	const showWidgetRef = useRef();

	// Create selected array
	const selectedArray = Array.isArray(selected) ? selected : [selected];

	// Handle share flow
	const handleAction = () => {
		setShowWidget(!showWidget);
	};

	// Handle actions on ref change
	useEffect(() => {

		// Define click outside function
		function handleClickOutside(event) {
			if (showWidgetRef.current && containerRef.current && !containerRef.current.contains(event.target)) {
				setShowWidget(false);
			}
		}

		// Bind the event listener
		document.addEventListener('mousedown', handleClickOutside);

		// Return effect
		return () => { // Unbind the event listener on clean up
			document.removeEventListener('mousedown', handleClickOutside);
		};

	}, [containerRef]);

	// Handle actions on widget state
	useEffect(() => {

		// Update ref
		showWidgetRef.current = showWidget;

	}, [showWidget]);

	// Handle actions on component load
	useEffect(() => {}, []);

	// Render component
	return (
		<S.Wrapper ref={containerRef} className={className}>

			{/* Action */}
			<S.ActionButton onClick={handleAction} className="animate" $isOpen={showWidget}>
				<Typography variation="button-small" weight="medium">{selectedTitle}</Typography>
				<FontAwesomeIcon className="animate" icon={['far', 'angle-down']} />
			</S.ActionButton>

			{/* Dropdown Widget */}
			<S.DropdownWidget className={showWidget ? 'animate show' : 'animate'} $width={widgetWidth}>
				<S.Header>
					<Typography tag="p" weight="bold" variation="2">{title}</Typography>
				</S.Header>
				<S.OptionContainer>
					{options.map((option) => (
						<S.Option
							className="animate"
							key={option.value}
							onClick={() => {

								// Hide widget if necessary
								if (!multipleSelection) {
									setTimeout(() => {
										setShowWidget(false);
									}, 100);
								}

								// Update selection
								onSelect(option);
							}}
						>
							<Typography tag="p" variation="2">{option.label}</Typography>
							{selectedArray.includes(option.value) && <FontAwesomeIcon icon={['far', 'check']} />}
						</S.Option>
					))}
				</S.OptionContainer>
			</S.DropdownWidget>
		</S.Wrapper>
	);
};


/**
 * Configuration
 */

FeedActionDropdown.displayName = 'FeedActionDropdown';
FeedActionDropdown.propTypes = {
	className: PropTypes.string,
	title: PropTypes.string,
	selected: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
	selectedTitle: PropTypes.string,
	options: PropTypes.arrayOf(PropTypes.shape()),
	onSelect: PropTypes.func,
	multipleSelection: PropTypes.bool,
	widgetWidth: PropTypes.number
};
FeedActionDropdown.defaultProps = {
	className: null,
	title: null,
	selected: null,
	selectedTitle: null,
	options: [],
	onSelect: null,
	multipleSelection: false,
	widgetWidth: 150
};
